import {useRelevanceParametersOptions} from '@/api/jobRecommendations/useRelevanceParametersOptions';
import {useGetRelevanceInformation} from '@/api/users/useGetRelevanceInformation';
import {useExtractPhraseConstants} from '@/hooks/useExtractPhraseConstants';
import {useSimpleModalLogic} from '@/hooks/useSimpleModalLogic';
import {LINKS} from '@/links';
import {useLinkedinSyncStatus} from '@/pages/User/pages/VerifyLinkedinAuthentication/hooks/useParams';
import {isDefined} from '@/utils/typeGuards/isDefined';
import {useCallback, useState} from 'react';
import {useIntl} from 'react-intl';
import messages, {linkedinSyncErrorMessages} from './messages';
import {CustomPlainButton} from './styled';
import {Props} from './types';
import {getLinkedinAuthorizationUrl} from './utils';

export function useFindMoreJobOpportunitiesEULogic({
    userId,
    organizationId,
    forDataCollectionView,
    onSuccess
}: Props) {
    const intl = useIntl();
    const phrases = useExtractPhraseConstants(messages);

    const detailedInformationModalLogic = useSimpleModalLogic();
    const relevanceFormModalLogic = useSimpleModalLogic();

    const {relevanceParametersOptions} = useRelevanceParametersOptions();
    const {userRelevanceInformation} = useGetRelevanceInformation(userId);

    const linkedInCaption = intl.formatMessage(messages.linkedInCaption, {
        seeDetailedInformationPlainButton: (
            <CustomPlainButton onClick={detailedInformationModalLogic.openModal}>
                {phrases.seeDetailedInformation}
            </CustomPlainButton>
        )
    });

    const [isRetrieveLinkedinDataLoading, setIsRetrieveLinkedinDataLoading] = useState(false);
    const onClickRetrieveLinkedinData = useCallback(() => {
        setIsRetrieveLinkedinDataLoading(true);
        if (forDataCollectionView) {
            const state = `${LINKS.readinessDataCollection}/${organizationId}`;
            window.location.href = getLinkedinAuthorizationUrl(state);
        }
    }, [forDataCollectionView, organizationId]);

    const onRelevanceFormSubmitSuccess = useCallback(() => {
        relevanceFormModalLogic.closeModal();
        onSuccess?.();
    }, [onSuccess, relevanceFormModalLogic]);

    const linkedinSyncStatus = useLinkedinSyncStatus();
    const linkedinSyncErrorPhrases = useExtractPhraseConstants(linkedinSyncErrorMessages);

    const errorTitle = isDefined(linkedinSyncStatus)
        ? linkedinSyncErrorPhrases[
              `${linkedinSyncStatus}_title` as keyof typeof linkedinSyncErrorPhrases
          ]
        : null;
    const errorDescription = isDefined(linkedinSyncStatus)
        ? linkedinSyncErrorPhrases[
              `${linkedinSyncStatus}_description` as keyof typeof linkedinSyncErrorPhrases
          ]
        : null;

    return {
        phrases,
        linkedInCaption,
        detailedInformationModalLogic,
        relevanceFormModalLogic,
        relevanceParametersOptions,
        userRelevanceInformation,
        isRetrieveLinkedinDataLoading,
        onClickRetrieveLinkedinData,
        onRelevanceFormSubmitSuccess,
        errorTitle,
        errorDescription
    };
}
